import { CalendarInputProps, CalendarType } from 'types/CalendarTypes'
import { ScheduleGroupProps } from 'types/CommonTypes'
import { DateCarpoolProps } from 'types/CommunityTypes'
import { UsersOutputProps } from 'types/UserTypes'
import { getCalendarDateRange } from './constants'

/**
 * Time utility functions for handling school schedule times:
 * - getCommunityArrivalTime: Gets default start time from a schedule group
 * - getCommunityDismissalTime: Gets default end time from a schedule group
 * - getDefaultedTime: Handles fallback to community time when no specific time is set
 * - resolveScheduleTime: Orchestrates the time resolution flow using the above helpers
 */
export const getCommunityArrivalTime = (ssg: ScheduleGroupProps[], sgn: string) => {
  const { schoolScheduleGroupStartTime } = ssg?.find((v) => v?.schoolScheduleGroupName === sgn) ?? {}
  const schoolStartTime: number = schoolScheduleGroupStartTime ?? 700
  return schoolStartTime
}

export const getCommunityDismissalTime = (ssg: ScheduleGroupProps[], sgn: string) => {
  const { schoolScheduleGroupEndTime } = ssg?.find((v) => v?.schoolScheduleGroupName === sgn) ?? {}
  const schoolEndTime: number = schoolScheduleGroupEndTime ?? 1500
  return schoolEndTime
}

export const getDefaultedTime = (communityTime: number, defaultTime?: number | null) => {
  // For now users can only specify not required or community (aka school dismissal) time
  // If user does not explicitly configure default time, the system default assumes community time
  const time: number = defaultTime === -1 ? -1 : defaultTime === null ? communityTime : communityTime
  return time
}

export const resolveScheduleTime = (
  label: string,
  ssg: ScheduleGroupProps[],
  sgn: string,
  defaultTime?: number | null,
  customTime?: number,
) => {
  const communityTime = label === 'arrival' ? getCommunityArrivalTime(ssg, sgn) : getCommunityDismissalTime(ssg, sgn)
  const resolvedDefaultTime = getDefaultedTime(communityTime, defaultTime)
  return customTime ?? resolvedDefaultTime
}

/*
 * Generate calendar events based on the type of calendar and the default time
 * @param eventType - It can be one of carpool requests or carpool management
 * @param defaultTime - It can be set to -1 (no carpool), or bell time
 * @param carpools - Map of carpool request times, and drivers for carpool management
 * @param communityUsers - List of all community user profile details
 * @param currentUser - Current user profile details
 * @returns The calendar events
 */
export const generateCalendarEvents = (
  eventType: CalendarType,
  {
    defaultTime,
    carpools,
    currentUserId,
    communityUsers,
  }: {
    defaultTime: number
    carpools: DateCarpoolProps
    currentUserId?: string
    communityUsers?: UsersOutputProps[]
  },
): CalendarInputProps[] => {
  const { startDate, endDate } = getCalendarDateRange()
  const daysInRange = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1

  return Array.from({ length: daysInRange }, (_, i) => {
    // Get the date for this iteration
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    const eventDate = d.toISOString().split('T')[0]
    const carpool = carpools[eventDate]

    // Use default time if no carpool is scheduled
    if (!carpool) return { eventDate, eventData: defaultTime === -1 ? null : {} }

    // Derive the event data as customized or default off or default on
    const carpoolTime: number = carpool.time
    const driverId: string = carpool.driverId

    if (eventType === CalendarType.CARPOOL && driverId && currentUserId) {
      const driver = communityUsers?.find((user) => user.userId === driverId)
      const carpoolDriver = {
        userId: driverId,
        userName: driver?.userName ?? 'Name Not Found',
        isCurrentUser: driverId === currentUserId,
      }

      return {
        eventDate,
        eventData: {
          isCarpoolRequest: !driverId,
          carpoolTime,
          carpoolDriver,
        },
      }
    } else {
      return {
        eventDate,
        eventData: {
          isCarpoolRequest: !driverId,
          carpoolTime,
        },
      }
    }
  })
}
